/**
 * "Tu cuenta no está activa" (T049; spec US5, escenario 2).
 *
 * Destino de `fermin:worker-inactive` (`403 WORKER_INACTIVE`): el admin desactivó al trabajador.
 * Es una pantalla **sin salida de autoservicio**: no ofrece solicitar vinculación ni escribir un
 * código, porque una solicitud de un trabajador `INACTIVE` no crea nada (la API responde `202` sin
 * fila) y ofrecerla haría creer que algo se está tramitando.
 *
 * "Cerrar sesión" es la única acción: cierra la sesión de este dispositivo si todavía existiera
 * (`POST /session/logout`) y limpia la caché local. Un error se ignora a propósito: la cuenta ya no
 * sirve y el objetivo es dejar el teléfono limpio para quien lo use después.
 */
import { LogOut } from 'lucide-react';

import { Button } from '../../components/ui/button';
import { Screen } from '../../components/ui/screen';
import { t } from '../../i18n';

import { useStaffLogout } from './api';

export interface InactiveScreenProps {
  /** Se llama tanto si el logout funcionó como si falló. */
  onLoggedOut: () => void;
}

export function InactiveScreen({ onLoggedOut }: InactiveScreenProps) {
  const logout = useStaffLogout();

  return (
    <Screen
      data-testid="access-inactive"
      align="center"
      title={t('access.inactive.title')}
      lead={t('access.inactive.description')}
      footer={
        <Button
          variant="secondary"
          loading={logout.isPending}
          loadingText={t('common.states.processing')}
          icon={<LogOut aria-hidden="true" size={20} />}
          onClick={() => logout.mutate(undefined, { onSettled: onLoggedOut })}
        >
          {t('access.inactive.logout')}
        </Button>
      }
    />
  );
}
